import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/resultados.css';
import api, { asArray, asObject } from '../lib/api';

function ResultadosPage() {
  const { relatorioId } = useParams();
  const navigate = useNavigate();
  const [relatorio, setRelatorio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('todos');

  const carregarRelatorio = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/relatorios/${relatorioId}`);
      const data = asObject(response.data);
      if (!data) {
        throw new Error('Resposta inválida da API de relatórios');
      }
      data.resultados = asArray(data.resultados);
      setRelatorio(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar relatório');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  }, [relatorioId]);

  useEffect(() => {
    carregarRelatorio();
  }, [carregarRelatorio]);

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await api.get(`/api/pdf/${relatorioId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_compliance_${relatorioId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
      alert('Erro ao baixar PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return <div className="loading">Carregando relatório...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>❌ Erro</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} className="btn-primary">
          Voltar
        </button>
      </div>
    );
  }

  if (!relatorio) {
    return <div className="error-container">Relatório não encontrado</div>;
  }

  const resultadosFiltrados = relatorio.resultados.filter(r => {
    if (filtroStatus === 'todos') return true;
    return r.status === filtroStatus;
  });

  const statusConfig = {
    conforme: { label: '✅ Conforme', color: 'green', bg: '#d4edda' },
    nao_conforme: { label: '❌ Não Conforme', color: 'red', bg: '#f8d7da' },
    alerta: { label: '⚠️ Alerta', color: 'orange', bg: '#fff3cd' },
    nao_aplicavel: { label: '⊘ Não Aplicável', color: 'gray', bg: '#e2e3e5' }
  };

  return (
    <div className="container">
      <div className="resultados-container">
        <div className="header-section">
          <h1>📊 Resultado da Análise</h1>
          <div className="header-info">
            <p><strong>Endereço:</strong> {relatorio.endereco}</p>
            <p><strong>Zona:</strong> {relatorio.zona}</p>
            <p><strong>Data:</strong> {new Date(relatorio.data_criacao).toLocaleDateString('pt-BR')}</p>
          </div>
        </div>

        {/* Resumo */}
        <section className="summary-section">
          <h2>Resumo</h2>
          <div className="summary-cards">
            <div className="summary-card total">
              <div className="card-value">{relatorio.total_verificacoes}</div>
              <div className="card-label">Total de Verificações</div>
            </div>
            <div className="summary-card conforme">
              <div className="card-value">{relatorio.conformes}</div>
              <div className="card-label">Conformes</div>
            </div>
            <div className="summary-card nao-conforme">
              <div className="card-value">{relatorio.nao_conformes}</div>
              <div className="card-label">Não Conformes</div>
            </div>
            <div className="summary-card alerta">
              <div className="card-value">{relatorio.alertas}</div>
              <div className="card-label">Alertas</div>
            </div>
          </div>
        </section>

        {/* Filtros */}
        <section className="filter-section">
          <h3>Filtrar por Status</h3>
          <div className="filter-buttons">
            <button
              className={filtroStatus === 'todos' ? 'active' : ''}
              onClick={() => setFiltroStatus('todos')}
            >
              Todos ({relatorio.resultados.length})
            </button>
            <button
              className={filtroStatus === 'conforme' ? 'active' : ''}
              onClick={() => setFiltroStatus('conforme')}
            >
              ✅ Conformes ({relatorio.conformes})
            </button>
            <button
              className={filtroStatus === 'nao_conforme' ? 'active' : ''}
              onClick={() => setFiltroStatus('nao_conforme')}
            >
              ❌ Não Conformes ({relatorio.nao_conformes})
            </button>
            <button
              className={filtroStatus === 'alerta' ? 'active' : ''}
              onClick={() => setFiltroStatus('alerta')}
            >
              ⚠️ Alertas ({relatorio.alertas})
            </button>
          </div>
        </section>

        {/* Resultados */}
        <section className="resultados-section">
          <h2>Detalhes das Verificações</h2>
          {resultadosFiltrados.length === 0 ? (
            <p className="no-results">Nenhum resultado encontrado para este filtro</p>
          ) : (
            <div className="resultados-list">
              {resultadosFiltrados.map((r, idx) => {
                const config = statusConfig[r.status] || statusConfig.nao_aplicavel;
                return (
                  <div
                    key={idx}
                    className="resultado-card"
                    style={{ backgroundColor: config.bg }}
                  >
                    <div className="resultado-header">
                      <h4>{r.item}</h4>
                      <span className="status-badge" style={{ color: config.color }}>
                        {config.label}
                      </span>
                    </div>
                    {r.lei && <p className="resultado-lei"><strong>Lei:</strong> {r.lei}</p>}
                    {r.artigo && <p className="resultado-artigo"><strong>Artigo:</strong> {r.artigo}</p>}
                    {r.valor_projeto !== null && r.valor_limite !== null && (
                      <div className="resultado-valores">
                        <p>
                          <strong>Projeto:</strong> {r.valor_projeto.toFixed(2)} |
                          <strong style={{ marginLeft: '10px' }}>Limite:</strong> {r.valor_limite.toFixed(2)}
                        </p>
                      </div>
                    )}
                    <p className="resultado-mensagem">{r.mensagem}</p>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Ações */}
        <section className="actions-section">
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            className="btn-primary"
          >
            {downloadingPdf ? 'Gerando PDF...' : '📥 Download PDF Completo'}
          </button>
          <button
            onClick={() => navigate('/')}
            className="btn-secondary"
          >
            ← Nova Análise
          </button>
        </section>
      </div>
    </div>
  );
}

export default ResultadosPage;
